// The Sculpted bench tool and a sculpted bench's card (docs/claude/lattice.md):
// two clicks along a room's faces lay a profile along them; the card sets its
// profile, ends, corner radius and colour, and holds its stack of cages.
import {
  type Lattice,
  type ProductInstance,
  type SculptedBench,
  type SculptEnd,
  type SculptHost,
  type SteamBenchPreset,
  type Vec2,
} from '../../shared/types';
import { DEFAULT_SAUNA_PROFILE_ID } from '../../shared/saunaProfilePresets';
import {
  CAGE_PICKS,
  type CagePick,
  copyLattice,
  defaultLattice,
  type FrameExtent,
  offsetOf,
  pointFreedom,
  refitLattice,
  shiftCagePoints,
} from '../lattice';
import {
  flashToolStatus,
  instLocked,
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  state,
  updateStatus,
  vp,
} from './core';
import {
  activeCageOf,
  benchLift,
  cagePickOf,
  cagePlan,
  newSculptedBench,
  previewOutline,
  sculptBenchAt,
  sculptColor,
  sculptEndAtLength,
  sculptExtent,
  sculptLayout,
  type SculptWarning,
  setActiveCage,
  setBenchProfile,
  setCagePick,
} from '../sculptBench';
import {
  buttonRow,
  colorRow,
  deleteRow,
  infoRow,
  noteRow,
  numberRow,
  parseMeasure,
  sectionTitle,
  selectRow,
} from './panelRows';
import {
  facePointAt,
  paramOf,
  runChain,
  sculptFace,
  sculptHostAt,
  stationOf,
} from '../sculptRun';
import {
  type BenchHover,
  deselectAll,
  instancesAt,
} from '../main';
import {
  pxAt,
  type ToolAim,
} from '../toolAim';
import {
  saunaProfiles,
  steamPresets,
} from './standards';
import {
  selectFeature,
  selectOne,
  selectRoomKeepingFeature,
} from '../selection';
import { isPolyRoom } from '../polyRoom';
import { removeFixture } from './roomFixtures';
import {
  instanceToWorld,
  toInstanceLocal,
} from '../geometry';
import { type SelGrip } from './grips';
import { tr } from '../i18n';

// ---- The tool ---------------------------------------------------------------

/** The tool's first click: the room and the end the bench starts at. */
let sculptStart: { instId: string; end: SculptEnd } | null = null;
/** The profile the tool lays, per kind of room (the list's default when unset). */
const toolPreset: { sauna?: string; steam?: string } = {};

/** Where the tool's pointer last stood: the room and the end a click there
 *  takes — what a number typed for the tool is measured on. */
let sculptHover: { instId: string; end: SculptEnd } | null = null;

/** The tool has its start and waits for the end. */
export const sculptToolStarted = (): boolean => !!sculptStart;
/** Forget the start (Esc, the tool left). */
export function dropSculptStart(): void { sculptStart = null; sculptHover = null; }

const isSteam = (room: ProductInstance): boolean => room.category === 'steamroom';
const byName = (a: SteamBenchPreset, b: SteamBenchPreset): number => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

/** The profiles a room's sculpted bench picks from: a steam room's steam
 *  profiles, a sauna's sauna profiles. */
export function sculptProfilesFor(room: ProductInstance): SteamBenchPreset[] {
  return [...(isSteam(room) ? steamPresets : saunaProfiles)].sort(byName);
}

function presetFor(room: ProductInstance): SteamBenchPreset | null {
  const list = sculptProfilesFor(room);
  const want = isSteam(room) ? toolPreset.steam : toolPreset.sauna ?? DEFAULT_SAUNA_PROFILE_ID;
  return list.find((p) => p.id === want) ?? list[0] ?? null;
}

/** What a click of the tool does at `aim`: remove the sculpted bench under it
 *  (before a start is picked), or take the face and station there as an end. */
function sculptTargetAt(aim: ToolAim): { room: ProductInstance; remove?: SculptedBench; end?: SculptEnd } | null {
  for (const id of instancesAt(aim.world, false)) {
    const room = state.drawing.instances.find((i) => i.id === id);
    if (!room || !room.autoBenches || instLocked(room) || !isPolyRoom(room)) continue;
    if (sculptStart && room.id !== sculptStart.instId) continue;
    const local = toInstanceLocal(room, aim.world);
    if (!sculptStart) {
      const on = sculptBenchAt(room, local, pxAt(aim, 4));
      if (on) return { room, remove: on };
    }
    const end = sculptHostAt(room, local);
    return end ? { room, end } : null;
  }
  return null;
}

/** The tool's hover: the bench a click removes (red); before the start, a mark
 *  where it would start; after it, the bench the click lays (red when a
 *  partition stands across its run). */
export function sculptBenchHoverAt(aim: ToolAim): BenchHover | null {
  const t = sculptTargetAt(aim);
  sculptHover = t?.end ? { instId: t.room.id, end: t.end } : null;
  if (!t) return null;
  if (t.remove) {
    const lay = sculptLayout(t.room, t.remove);
    return lay ? { instId: t.room.id, polys: [lay.outline], kind: 'delete' } : null;
  }
  const preset = presetFor(t.room);
  if (!preset || !t.end) return null;
  if (sculptStart) {
    const ring = previewOutline(t.room, newSculptedBench('preview', sculptStart.end, t.end, preset));
    if (!ring) return null;
    const blocked = !!runChain(t.room, sculptStart.end, t.end)?.blocked;
    return { instId: t.room.id, polys: [ring], kind: blocked ? 'delete' : 'add' };
  }
  const f = sculptFace(t.room, t.end.host);
  if (!f) return null;
  const depth = sculptExtent({ length: 0 }, preset.profile).out[1], p = paramOf(f, t.end.s), w = 30;
  return { instId: t.room.id, polys: [[facePointAt(f, p - w, 0), facePointAt(f, p + w, 0), facePointAt(f, p + w, depth), facePointAt(f, p - w, depth)]], kind: 'add' };
}

export function downSculptBenchTool(e: MouseEvent, aim: ToolAim): boolean {
  if (state.tool !== 'sculpt-bench' || e.button !== 0) return false;
  const t = sculptTargetAt(aim);
  if (!t) return true;
  if (t.remove) {
    pushHistory();
    removeFixture(t.room, t.remove.id);
    selectRoomKeepingFeature(state, t.room.id);
    renderProperties();
    invalidate();
    return true;
  }
  if (!t.end) return true;
  if (!sculptStart) {
    sculptStart = { instId: t.room.id, end: t.end };
    invalidate();
    return true;
  }
  layBench(t.room, t.end);
  return true;
}

/** The tool's bench laid from its start to `end`, one undo step, and selected. */
function layBench(room: ProductInstance, end: SculptEnd): void {
  const preset = presetFor(room);
  if (!sculptStart || !preset) return;
  const sb = newSculptedBench(newId('sb'), sculptStart.end, end, preset);
  if (!previewOutline(room, sb)) {
    flashToolStatus(tr('The bench needs some length: click further along.'));
    return;
  }
  pushHistory();
  (room.sculptedBenches ??= []).push(sb);
  sculptStart = null;
  deselectAll();
  selectOne(state, 'instance', room.id);
  selectFeature(state, { kind: 'fixture', instId: room.id, id: sb.id });
  state.benchAddHover = null;
  renderProperties();
  invalidate();
}

/** A digit typed on the drawing goes to the command bar while the tool is armed:
 *  the start's distance along its face, then the bench's length. */
export const sculptTakesNumber = (): boolean => state.tool === 'sculpt-bench';

/** A number typed for the tool (parseMeasure): before the start, how far along
 *  the face under the pointer it starts, from that face's start; after it, how
 *  long the bench is (its middle line, the card's Length), walking toward the
 *  face under the pointer. False when it is not the tool's, or nothing is under
 *  the pointer to measure on. */
export function setSculptNumber(text: string): boolean {
  if (!sculptTakesNumber()) return false;
  const v = parseMeasure(text);
  if (v == null) return false;
  const hover = sculptHover;
  const room = hover && state.drawing.instances.find((i) => i.id === hover.instId);
  if (!hover || !room || (sculptStart && sculptStart.instId !== room.id)) {
    flashToolStatus(tr('Point at the face the figure is measured on, then type it.'));
    return true;
  }
  if (!sculptStart) {
    const f = sculptFace(room, hover.end.host);
    if (!f) return true;
    sculptStart = { instId: room.id, end: { host: hover.end.host, s: stationOf(f, Math.max(f.lo, Math.min(f.hi, f.lo + v))) } };
    updateStatus();
    invalidate();
    return true;
  }
  const preset = presetFor(room);
  const end = preset && sculptEndAtLength(room, { start: sculptStart.end, profile: preset.profile }, hover.end, v);
  if (!end) { flashToolStatus(tr('The bench needs some length: click further along.')); return true; }
  layBench(room, end);
  return true;
}

/** The tool's status-bar prompt. */
export function sculptToolHint(): string {
  return sculptStart
    ? 'sculpted bench — click where it ends, along the walls from its start, or type its length toward the pointer and press Enter (Esc to start again)'
    : 'sculpted bench — click a wall, a heater wall or a partition face where the bench starts, or type how far along the face under the pointer it starts and press Enter; click a sculpted bench to remove it';
}

// ---- The card -----------------------------------------------------------------

/** Where an end stands, as the card says it: its wall or partition face. */
function hostLabel(inst: ProductInstance, host: SculptHost): string {
  if ('wall' in host) return `${tr('wall')} ${host.wall + 1}`;
  const i = (inst.innerWalls ?? []).findIndex((w) => w.id === host.partition);
  return `${tr('inner wall')} ${i + 1} · ${host.face === 'a' ? 'A' : 'B'}`;
}

/** What a sculpted bench's warnings say on its card. */
const SCULPT_WARNING_TEXT: Record<SculptWarning, () => string> = {
  blocked: () => tr('A partition stands across the run: the bench stops at its face.'),
  clipped: () => tr('A cage reaches past the bench’s ends.'),
  fold: () => tr('A cage folds the bench through itself: move its points back.'),
};

// ---- The plan's grips ---------------------------------------------------------

/** The selected sculpted bench's grips on the plan: one on each column of its
 *  active cage (its points stacked up) that may move along or out. Dragging one
 *  moves every point of the column by as much in the run's frame, read off the
 *  run (`locate`); a back column slides along the run alone (`setOffset`). */
export function cageGrips(inst: ProductInstance, sb: SculptedBench): SelGrip[] {
  const lay = sculptLayout(inst, sb), cage = activeCageOf(sb);
  if (!lay || !cage || cage.enabled === false) return [];
  const ext = lay.extent;
  const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const cols = cagePlan(lay, cage).columns.filter((col) => {
    for (let k = 0; k < cage.nz; k++) { const f = pointFreedom(cage, ext, col.i, col.j, k); if (f && (f.along || f.out)) return true; }
    return false;
  });
  const all = cols.map((col) => W(col.at));
  const pick = cagePickOf(sb.id);
  return cols.map((col, n): SelGrip => {
    const from = lay.run.locate(col.at);
    // Its column, and the columns its pick moves with it: its row's, or its cross-section's.
    const group: Array<[number, number, number]> = [];
    for (let i = 0; i < cage.nx; i++) for (let j = 0; j < cage.ny; j++) {
      const mine = pick === 'row' ? j === col.j : pick === 'across' ? i === col.i : i === col.i && j === col.j;
      if (mine) for (let k = 0; k < cage.nz; k++) group.push([i, j, k]);
    }
    const offs = group.map(([i, j, k]) => offsetOf(cage, i, j, k));
    return {
      at: all[n], others: all.filter((_p, m) => m !== n), self: inst.id,
      apply: (q) => {
        const to = lay.run.locate(toInstanceLocal(inst, q), from);
        shiftCagePoints(cage, ext, group, offs, [to.along - from.along, to.out - from.out, null]);
      },
    };
  });
}

/** How near a face's end (screen px) an end grip holds there. */
const END_STOP_PX = 10;

/** The selected sculpted bench's grips at its two ends, half way through its
 *  depth: one dragged along the faces — onto the next wall round a corner, onto a
 *  partition's face — by 10 mm from its face's start (Alt: free), held at the
 *  face's ends near the pointer; or a figure typed (its distance from its face's
 *  start, as the card's Start / End) moves that end alone. A place where no run
 *  resolves leaves it where it was. */
export function sculptEndGrips(inst: ProductInstance, sb: SculptedBench): SelGrip[] {
  if (instLocked(inst)) return [];
  const lay = sculptLayout(inst, sb);
  if (!lay) return [];
  const mid = lay.run.depth / 2;
  const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const tips = [lay.run.at(0, mid), lay.run.at(lay.run.length, mid)];
  const stored = (e: SculptEnd): Vec2 | null => { const f = sculptFace(inst, e.host); return f ? facePointAt(f, paramOf(f, e.s), mid) : null; };
  const d = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
  const ps = stored(sb.start);
  const startFirst = !ps || d(ps, tips[0]) <= d(ps, tips[1]);
  const put = (which: 'start' | 'end', end: SculptEnd): void => {
    if (previewOutline(inst, { ...sb, [which]: end })) sb[which] = end;
  };
  return (['start', 'end'] as const).map((which): SelGrip => ({
    at: W(tips[(which === 'start') === startFirst ? 0 : 1]), others: [], self: inst.id, apply: () => {},
    drag: (world, free) => {
      const e = sculptHostAt(inst, toInstanceLocal(inst, world));
      const f = e && sculptFace(inst, e.host);
      if (!e || !f) return;
      const p = paramOf(f, e.s), tol = END_STOP_PX / vp.scale;
      const stop = free ? undefined : [f.lo, f.hi].find((t) => Math.abs(t - p) <= tol);
      const q = stop ?? (free ? p : f.lo + Math.round((p - f.lo) / 10) * 10);
      put(which, { host: e.host, s: stationOf(f, Math.max(f.lo, Math.min(f.hi, q))) });
    },
    figure: {
      label: which === 'start' ? 'Start (mm)' : 'End (mm)',
      set: (v) => {
        const f = sculptFace(inst, sb[which].host);
        if (f) put(which, { host: sb[which].host, s: stationOf(f, Math.max(f.lo, Math.min(f.hi, f.lo + v))) });
        return null;
      },
    },
    measure: () => {
      const f = sculptFace(inst, sb[which].host);
      if (!f) return null;
      const p = paramOf(f, sb[which].s);
      return { a: W(facePointAt(f, f.lo, 0)), b: W(facePointAt(f, p, 0)), len: p - f.lo };
    },
  }));
}

/** A sculpted bench's card: its profile, ends, corner radius and colour, its
 *  cages, its warnings, then Delete. */
export function renderSculptBenchPanel(body: HTMLElement, inst: ProductInstance, sb: SculptedBench): void {
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  const lay = sculptLayout(inst, sb);
  body.appendChild(sectionTitle('Sculpted bench'));
  const profiles = sculptProfilesFor(inst);
  const known = profiles.some((p) => p.id === sb.profilePresetId);
  const options: Array<[string, string]> = profiles.map((p) => [p.id, p.name]);
  if (!known) options.unshift(['', 'Own profile']);
  body.appendChild(selectRow('Profile', options, known ? sb.profilePresetId! : '', (v) => {
    const p = profiles.find((x) => x.id === v);
    if (!p) return;
    if (isSteam(inst)) toolPreset.steam = p.id; else toolPreset.sauna = p.id;
    edit(() => setBenchProfile(sb, p));
  }));
  body.appendChild(numberRow('Height from ground (mm)', Math.round(benchLift(sb)), (v) => edit(() => {
    const h = Math.max(0, Math.round(v));
    if (h > 0) sb.heightFromGround = h; else delete sb.heightFromGround;
  }), { commit: true }));
  if (lay) body.appendChild(infoRow('Length', `${Math.round(lay.run.length)} mm`));
  for (const which of ['start', 'end'] as const) {
    const end = sb[which];
    const f = sculptFace(inst, end.host);
    body.appendChild(infoRow(which === 'start' ? 'From' : 'To', hostLabel(inst, end.host)));
    if (!f) continue;
    body.appendChild(numberRow(which === 'start' ? 'Start (mm)' : 'End (mm)', Math.round(paramOf(f, end.s) - f.lo), (v) => edit(() => {
      end.s = stationOf(f, Math.max(f.lo, Math.min(f.hi, f.lo + v)));
    }), { commit: true }));
  }
  if (lay) {
    body.appendChild(numberRow('Corner radius (mm)', Math.round(lay.run.cornerRadius), (v) => edit(() => { sb.cornerRadius = Math.max(0, Math.round(v)); }), {
      commit: true, onClear: () => edit(() => { delete sb.cornerRadius; }),
    }));
  }
  body.appendChild(colorRow('Colour', sculptColor(inst, sb), (hex) => { sb.color = hex; renderProperties(); invalidate(); }, {
    onClear: () => { pushHistory(); delete sb.color; invalidate(); return sculptColor(inst, sb); },
  }));
  if (lay) appendCagesCard(body, sb, lay.extent, edit);
  for (const w of lay?.warnings ?? []) body.appendChild(noteRow(SCULPT_WARNING_TEXT[w](), 'warn', true));
  body.appendChild(deleteRow(() => { pushHistory(); removeFixture(inst, sb.id); renderProperties(); invalidate(); }));
}

/** What the card calls each pick (`CagePick`): what a drag of one point moves with it. */
const CAGE_PICK_TEXT: Record<CagePick, string> = {
  point: 'The point alone',
  row: 'Its row, along the bench',
  column: 'Its column, up',
  across: 'Its cross-section',
};

/** A cage's name on the card: the one entered, else its place in the stack. */
const cageName = (c: Lattice, i: number): string => c.name || `${tr('Cage')} ${i + 1}`;

/** The bench's cages, applied top to bottom: each one picked to edit, renamed,
 *  switched off, moved, copied, reset or deleted; the picked one's counts and
 *  stretch along the bench; Add cage. */
function appendCagesCard(body: HTMLElement, sb: SculptedBench, extent: FrameExtent, edit: (fn: () => void) => void): void {
  body.appendChild(sectionTitle('Cages'));
  const list = sb.lattices ?? [];
  const active = activeCageOf(sb);
  const small = (text: string, title: string, onClick: () => void, disabled = false): HTMLButtonElement => {
    const b = document.createElement('button');
    b.className = 'small';
    b.textContent = tr(text);
    b.title = tr(title);
    b.disabled = disabled;
    b.addEventListener('click', onClick);
    return b;
  };
  list.forEach((cage, i) => {
    const row = document.createElement('div');
    row.className = 'prop-row sculpt-cage';
    row.dataset.cage = cage.id;
    const pick = document.createElement('input');
    pick.type = 'radio';
    pick.name = `cage-${sb.id}`;
    pick.checked = cage.id === active?.id;
    pick.title = tr('Edit this cage');
    pick.addEventListener('change', () => { setActiveCage(sb.id, cage.id); renderProperties(); invalidate(); });
    const name = document.createElement('input');
    name.type = 'text';
    name.value = cage.name ?? '';
    name.placeholder = cageName({ ...cage, name: undefined }, i);
    name.title = tr('Name');
    name.addEventListener('change', () => edit(() => { const v = name.value.trim(); if (v) cage.name = v; else delete cage.name; }));
    const on = document.createElement('input');
    on.type = 'checkbox';
    on.checked = cage.enabled !== false;
    on.title = tr('Bends the bench');
    on.addEventListener('change', () => edit(() => { if (on.checked) delete cage.enabled; else cage.enabled = false; }));
    const move = (by: number): void => edit(() => { const l = sb.lattices!; const [x] = l.splice(i, 1); l.splice(i + by, 0, x); });
    row.append(
      pick, name, on,
      small('Up', 'Move up', () => move(-1), i === 0),
      small('Down', 'Move down', () => move(1), i === list.length - 1),
      small('Copy', 'Duplicate', () => edit(() => { const cp = copyLattice(cage, newId('lc')); sb.lattices!.splice(i + 1, 0, cp); setActiveCage(sb.id, cp.id); })),
      small('Reset', 'Put every point back', () => edit(() => { delete cage.offsets; }), !cage.offsets),
      small('Delete', 'Delete', () => edit(() => { sb.lattices = sb.lattices!.filter((x) => x.id !== cage.id); if (!sb.lattices.length) delete sb.lattices; })),
    );
    body.appendChild(row);
  });
  if (active) {
    const idx = list.indexOf(active);
    const refit = (next: Parameters<typeof refitLattice>[2]): void => edit(() => { sb.lattices![idx] = refitLattice(active, extent, next); });
    body.appendChild(numberRow('Points along', active.nx, (v) => refit({ nx: v }), { commit: true }));
    body.appendChild(numberRow('Points out', active.ny, (v) => refit({ ny: v }), { commit: true }));
    body.appendChild(numberRow('Points up', active.nz, (v) => refit({ nz: v }), { commit: true }));
    const along = active.box.along;
    const boxAlong = (lo: number, hi: number): void => refit({ box: { ...active.box, along: [Math.round(lo), Math.round(Math.max(lo + 50, hi))] } });
    body.appendChild(numberRow('Cage from (mm)', Math.round(along[0]), (v) => boxAlong(v, along[1]), { commit: true }));
    body.appendChild(numberRow('Cage to (mm)', Math.round(along[1]), (v) => boxAlong(along[0], v), { commit: true }));
    body.appendChild(buttonRow('Fit the whole bench', () => refit({ box: { along: [...extent.along], out: [...extent.out], up: [...extent.up] } })));
    body.appendChild(infoRow('Points moved', String(Object.keys(active.offsets ?? {}).length)));
    body.appendChild(selectRow('A drag moves', CAGE_PICKS.map((p) => [p, CAGE_PICK_TEXT[p]]), cagePickOf(sb.id), (v) => {
      setCagePick(sb.id, v as CagePick);
      renderProperties();
      invalidate();
    }));
    body.appendChild(noteRow('Drag the cage’s points in 3D, in a section or on the plan; in 3D, the arrows on the point last pressed move it along the bench, out or up.'));
  }
  body.appendChild(buttonRow('Add cage', () => edit(() => {
    const c = defaultLattice(newId('lc'), extent);
    (sb.lattices ??= []).push(c);
    setActiveCage(sb.id, c.id);
  })));
}
